import { useData } from "../data/DataContext";
import { useUI } from "../data/UIContext";
import { ContentHeader } from "../components/ContentHeader";
import { OPERATIONS } from "../nav";
import type { CoverageRow, CoverageValue } from "../api/types";

const TIER_HELP =
  "Criticality tier — P0 (most critical: customer-facing money path, page immediately) · P1 high · P2 medium · P3 low";

const AXIS_LABELS: Record<string, string> = {
  has_dashboard:         "Dashboard",
  has_alert:             "Alert",
  has_runbook:           "Runbook",
  has_slo:               "SLO",
  has_owner:             "Owner",
  has_crashlytics:       "Crashlytics",
  has_synthetic:         "Synthetic",
  has_webhook_freshness: "Webhook freshness",
};

export function CoverageTab() {
  const data = useData();
  const ui = useUI();
  const meta = OPERATIONS.find((e) => e.id === "coverage")!;
  const cov = data.coverage;

  if (!cov) {
    return (
      <>
        <ContentHeader title={meta.label} description={meta.desc} />
        <div className="empty">
          coverage.json not loaded — run <code>python3 tools/alert-coverage/coverage.py --emit-json</code>
        </div>
      </>
    );
  }

  const findEntity = (row: CoverageRow) => {
    const list = (data.commandCenter?.estates as Record<string, { id: string; name: string }[]>)?.[row.estate] ?? [];
    return list.find((x) => x.id === row.id || x.name === row.name);
  };

  return (
    <>
      <ContentHeader title={meta.label} description={meta.desc} />
      <div className="cov-summary">
        {Object.entries(cov.gaps || {}).map(([axis, n]) => {
          const cls = n === 0 ? "ok" : n > 10 ? "crit" : "warn";
          return (
            <span key={axis} className={`cov-pill ${cls}`}>
              <span className="dot" /><strong>{n}</strong> {AXIS_LABELS[axis] ?? axis}
            </span>
          );
        })}
      </div>
      <p className="legend-note">
        ✓ = present · ✗ = missing · ? = unknown · — = not applicable. Each column is an observability signal a service should have.
      </p>
      <table className="cov-matrix">
        <thead>
          <tr>
            <th>Entity</th><th>Estate</th><th>Tier</th>
            {cov.axes.map((a) => <th key={a}>{AXIS_LABELS[a] ?? a}</th>)}
          </tr>
        </thead>
        <tbody>
          {cov.rows.map((r) => {
            const ent = findEntity(r);
            return (
              <tr key={r.id}>
                <td>
                  <button className="cov-name"
                          onClick={() => ent && ui.openEntity(ent as never)}
                          disabled={!ent}>{r.name}</button>
                </td>
                <td>{r.estate}</td>
                <td>
                  {r.tier
                    ? <span className={`tier-badge tier-${r.tier}`} title={TIER_HELP} style={{ cursor: "help" }}>{r.tier}</span>
                    : "—"}
                </td>
                {cov.axes.map((a) => {
                  const v = (r as unknown as Record<string, CoverageValue>)[a];
                  return (
                    <td key={a} className={`cov-cell ${v ?? "n_a"}`} title={`${a}=${v}`}>
                      {cellMark(v)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function cellMark(v?: CoverageValue): string {
  if (v === "yes") return "✓";
  if (v === "no") return "✗";
  if (v === "unknown") return "?";
  return "—";
}
